import React, { useMemo } from 'react';
import { downloadExcel } from '../../../../utils/excel';
import { categoryList, productList, TraditionalStores } from '../../../common';

/** ≥80 good, ≥50 attention, otherwise bad. */
export const distributionTone = (percentage) => (Number(percentage) >= 80 ? 'success' : Number(percentage) >= 50 ? 'warning' : 'danger');

/**
 * Numeric distribution for the client profile: product presence in the latest audit of each store,
 * grouped by category, with an optional store-type filter for the traditional channel.
 */
export function useShareData({ checks, clientName, storeTypes, channel }) {
    const [filter, setFilter] = React.useState('all');
    const [expandedCategories, setExpandedCategories] = React.useState({});

    const storeChecks = channel === 'moderno' || filter === 'all' ? checks : checks?.filter((check) => filter === check.storeId?.storeType);
    const availableProds = storeTypes?.find((st) => st?.value === filter)?.products || [];
    const availableCats = productList?.filter((prod) => availableProds?.includes(prod.value))?.map((p) => p.category) || [];

    // Get latest store check per store
    const latestStoreChecks = useMemo(() => {
        const storeMap = new Map();

        if (!storeChecks) return [];
        storeChecks?.forEach((check) => {
            const storeId = check.storeId?._id;
            if (!storeMap.has(storeId) || new Date(check.startTime) > new Date(storeMap.get(storeId).startTime)) {
                storeMap.set(storeId, check);
            }
        });

        return Array.from(storeMap.values());
    }, [storeChecks]);

    const storeInfo = latestStoreChecks?.map((check) => ({
        store: check.storeId?.name || 'Unknown Store',
        products: check?.products?.filter((p) => p?.isAvailable).map((pr) => productList.find((prod) => pr?.name === prod.name)?.value) || []
    }));

    const productCountMap = new Map();

    storeInfo?.forEach(({ products }) => {
        products.forEach((product) => {
            productCountMap.set(product, (productCountMap.get(product) || 0) + 1);
        });
    });

    const productCounts = Array.from(productCountMap.entries())?.map(([product, count]) => ({
        product,
        count
    }));

    const download = () => {
        const prods = productList?.filter((p) => storeInfo[0]?.products?.includes(p.value)).map((prod) => prod?.value);
        const headers = ['Pisos', ...prods];

        const info = storeInfo?.map(({ store, products }) => {
            const productData = {};
            prods.forEach((prod) => {
                productData[prod] = products.includes(prod) ? 'X' : '';
            });
            return { Pisos: store, ...productData };
        });

        const totalData = prods.map((prod) => {
            const count = storeInfo?.reduce((sum, { products }) => {
                return sum + (products.includes(prod) ? 1 : 0);
            }, 0);
            return { [prod]: count };
        });

        const total = totalData.reduce((acc, obj) => ({ ...acc, ...obj }), {});

        const percentageData = prods.map((prod) => {
            const count = storeInfo?.reduce((sum, { products }) => {
                return sum + (products.includes(prod) ? 1 : 0);
            }, 0);
            return { [prod]: `${((count / storeInfo.length) * 100).toFixed(2)}%` };
        });

        const percentage = percentageData.reduce((acc, obj) => ({ ...acc, ...obj }), {});

        const totalRow = { Pisos: 'Total', ...total };
        const percentageRow = { Pisos: 'Porcentaje', ...percentage };
        const combinedData = [...info, totalRow, percentageRow];

        const name = clientName?.replace(/ /g, '_').toLowerCase() || 'cliente';
        const client = name.split('.')[0] || 'Pisos de Venta';

        downloadExcel(headers, combinedData, client);
    };

    const storeTypesAvailable = channel === 'tradicional' ? TraditionalStores.map((st) => st.value) : storeChecks?.flatMap((st) => st.storeId?.storeType) || [];
    const storeTypeOptions = TraditionalStores?.filter((tradTypes) => storeTypesAvailable?.includes(tradTypes?.value)) || [];

    const toggleCategory = (index) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // One entry per category with its availability % and per-product rows.
    const categories = (filter !== 'all' ? categoryList?.filter((cat) => availableCats?.includes(cat?.name)) : categoryList).map((category, i) => {
        const categoryProducts =
            storeInfo.length > 0 ? productList.filter((prod) => storeInfo.some((s) => s.products.includes(prod.value)) && prod.category === category.name) : [];
        const totalCategoryCount = categoryProducts.reduce((sum, product) => {
            const count = productCounts.find((p) => p.product === product.value)?.count || 0;
            return sum + count;
        }, 0);
        const totalPossibleChecks = latestStoreChecks?.length * (categoryProducts.length || 1);
        const categoryPercentage = totalPossibleChecks > 0 ? ((totalCategoryCount / totalPossibleChecks) * 100).toFixed(0) : '0';

        const productsData = categoryProducts.map((product, idx) => {
            const count = productCounts.find((p) => p.product === product.value)?.count || 0;
            const checksCount = latestStoreChecks?.length || 0;
            const percentage = checksCount > 0 ? ((count * 100) / checksCount).toFixed(0) : '0';
            return {
                key: idx,
                product: product.name,
                count,
                checks: checksCount,
                percentage: parseFloat(percentage)
            };
        });

        return { index: i, category, categoryProducts, categoryPercentage, productsData, isOpen: !!expandedCategories[i] };
    });

    return { filter, setFilter, latestStoreChecks, download, storeTypeOptions, toggleCategory, categories };
}
